import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Branding } from "./types";

/**
 * Pulls Latest Conference branding for a given region name.
 * Works for any year — picks the row with the largest conference_year
 * so no code changes are needed as the year rolls over.
 */
export function useConferenceBranding(regionName: string) {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!regionName || !regionName.trim()) {
        setBranding(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {

        const base = regionName.replace(/\s*region\s*/i, "").trim();
        const { data: kits } = await supabase
          .from("conference_kit_events")
          .select("region, region_id, title, theme, scripture, venue_name, venue_address, city, state, start_date, end_date, flyer_url, conference_year")
          .ilike("region", `${base}%`)
          .order("conference_year", { ascending: false })
          .limit(1);
        const kit = (kits as any[])?.[0] || null;
        const year = kit?.conference_year || new Date().getFullYear();

        // Fallback watermark: hero image from Latest Conference for that region
        let watermark: string | null = kit?.flyer_url || null;
        if (!watermark && kit?.region_id) {
          const { data: conf } = await supabase
            .from("conferences")
            .select("flyer_url")
            .eq("region_id", kit.region_id)
            .maybeSingle();
          watermark = (conf as any)?.flyer_url || null;
        }

        setBranding({
          year,
          region: regionName,
          title: kit?.title || `OFUESI Annual Regional Conference ${year}`,
          theme: kit?.theme || null,
          scripture: kit?.scripture || null,
          venueName: kit?.venue_name || null,
          venueAddress: kit?.venue_address || null,
          city: kit?.city || null,
          state: kit?.state || null,
          startDate: kit?.start_date || null,
          endDate: kit?.end_date || null,
          watermarkUrl: watermark,
          logoUrl: null, // BadgeCard falls back to bundled transparent asset
          website: "ofuesi.org",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [regionName]);

  return { branding, loading };
}
